// ── Web Audio API sound utilities ─────────────────────────
// Ambient sounds: ocean, campfire, cafe, forest (synthesized)
// Chimes: start, end, break

let audioCtx = null;
let activeNodes = []; // all nodes that need .stop() / .disconnect()
let noiseGain = null;
let crackleTimer = null;
let birdTimer = null;

function getCtx() {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return audioCtx;
}

// ── Noise buffer builders ───────────────────────────────────

function buildWhiteBuffer(ctx, seconds = 3) {
  const len = seconds * ctx.sampleRate;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  return buf;
}

function buildPinkBuffer(ctx, seconds = 3) {
  const len = seconds * ctx.sampleRate;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let b0=0,b1=0,b2=0,b3=0,b4=0,b5=0,b6=0;
  for (let i = 0; i < len; i++) {
    const w = Math.random() * 2 - 1;
    b0 = 0.99886*b0 + w*0.0555179; b1 = 0.99332*b1 + w*0.0750759;
    b2 = 0.96900*b2 + w*0.1538520; b3 = 0.86650*b3 + w*0.3104856;
    b4 = 0.55000*b4 + w*0.5329522; b5 = -0.7616*b5 - w*0.0168980;
    d[i] = (b0+b1+b2+b3+b4+b5+b6+w*0.5362) / 7;
    b6 = w * 0.115926;
  }
  return buf;
}

function buildBrownBuffer(ctx, seconds = 3) {
  const len = seconds * ctx.sampleRate;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < len; i++) {
    const w = Math.random() * 2 - 1;
    d[i] = (last + 0.02 * w) / 1.02;
    last = d[i];
    d[i] *= 3.5;
  }
  return buf;
}

function makeLoopSource(ctx, buf) {
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  return src;
}

// ── OCEAN ───────────────────────────────────────────────────
// Brown noise → lowpass filter, amplitude modulated by two slow LFOs (wave in/out)

function startOcean(ctx, masterGain) {
  const brown = buildBrownBuffer(ctx, 4);
  const src = makeLoopSource(ctx, brown);

  const lpf = ctx.createBiquadFilter();
  lpf.type = 'lowpass';
  lpf.frequency.value = 700;
  lpf.Q.value = 0.8;

  // Wave envelope: two oscillators at different rates
  const lfo1 = ctx.createOscillator();
  const lfo1g = ctx.createGain();
  lfo1.frequency.value = 0.08;
  lfo1g.gain.value = 0.25;

  const lfo2 = ctx.createOscillator();
  const lfo2g = ctx.createGain();
  lfo2.frequency.value = 0.13;
  lfo2g.gain.value = 0.15;

  const carrierGain = ctx.createGain();
  carrierGain.gain.value = 0.55;

  lfo1.connect(lfo1g); lfo1g.connect(carrierGain.gain);
  lfo2.connect(lfo2g); lfo2g.connect(carrierGain.gain);

  src.connect(lpf); lpf.connect(carrierGain); carrierGain.connect(masterGain);

  // Second layer: higher-pitched surf
  const pink = buildPinkBuffer(ctx, 4);
  const src2 = makeLoopSource(ctx, pink);
  const lpf2 = ctx.createBiquadFilter();
  lpf2.type = 'bandpass';
  lpf2.frequency.value = 1200;
  lpf2.Q.value = 2;
  const g2 = ctx.createGain();
  g2.gain.value = 0.12;

  const lfo3 = ctx.createOscillator();
  const lfo3g = ctx.createGain();
  lfo3.frequency.value = 0.07;
  lfo3g.gain.value = 0.1;
  lfo3.connect(lfo3g); lfo3g.connect(g2.gain);

  src2.connect(lpf2); lpf2.connect(g2); g2.connect(masterGain);

  [src, src2, lfo1, lfo2, lfo3].forEach(n => n.start());
  activeNodes.push(src, src2, lfo1, lfo2, lfo3, lpf, lpf2, lfo1g, lfo2g, lfo3g, carrierGain, g2);
}

// ── CAMPFIRE ────────────────────────────────────────────────
// Low brown noise base + random crackle pops (scheduled gain spikes)

function startCampfire(ctx, masterGain) {
  // Base: warm low rumble
  const brown = buildBrownBuffer(ctx, 4);
  const base = makeLoopSource(ctx, brown);
  const lpf = ctx.createBiquadFilter();
  lpf.type = 'lowpass';
  lpf.frequency.value = 500;
  const baseGain = ctx.createGain();
  baseGain.gain.value = 0.45;
  base.connect(lpf); lpf.connect(baseGain); baseGain.connect(masterGain);
  base.start();
  activeNodes.push(base, lpf, baseGain);

  // Crackle layer: white noise through gain that gets random pops
  const white = buildWhiteBuffer(ctx, 4);
  const crackSrc = makeLoopSource(ctx, white);
  const crackBpf = ctx.createBiquadFilter();
  crackBpf.type = 'bandpass';
  crackBpf.frequency.value = 2500;
  crackBpf.Q.value = 3;
  const crackGain = ctx.createGain();
  crackGain.gain.value = 0.001;
  crackSrc.connect(crackBpf); crackBpf.connect(crackGain); crackGain.connect(masterGain);
  crackSrc.start();
  activeNodes.push(crackSrc, crackBpf, crackGain);

  // Schedule random crackles
  function scheduleCrackles() {
    if (!audioCtx) return;
    const now = ctx.currentTime;
    const count = 3 + Math.floor(Math.random() * 8);
    for (let i = 0; i < count; i++) {
      const t = now + Math.random() * 2.5;
      const vol = 0.08 + Math.random() * 0.18;
      try {
        crackGain.gain.setValueAtTime(vol, t);
        crackGain.gain.exponentialRampToValueAtTime(0.001, t + 0.008 + Math.random() * 0.025);
      } catch(_) {}
    }
    crackleTimer = setTimeout(scheduleCrackles, 2000);
  }
  scheduleCrackles();
}

// ── CAFE ────────────────────────────────────────────────────
// Pink noise + light reverb (two delay lines) → muffled crowd ambience

function startCafe(ctx, masterGain) {
  const pink = buildPinkBuffer(ctx, 4);
  const src = makeLoopSource(ctx, pink);

  const lpf = ctx.createBiquadFilter();
  lpf.type = 'lowpass';
  lpf.frequency.value = 2200;
  const hpf = ctx.createBiquadFilter();
  hpf.type = 'highpass';
  hpf.frequency.value = 120;

  const dry = ctx.createGain();
  dry.gain.value = 0.35;

  // Delay 1 (echo at ~120ms)
  const delay1 = ctx.createDelay(0.5);
  delay1.delayTime.value = 0.12;
  const fb1 = ctx.createGain();
  fb1.gain.value = 0.25;
  const wet1 = ctx.createGain();
  wet1.gain.value = 0.12;

  // Delay 2 (echo at ~220ms)
  const delay2 = ctx.createDelay(0.5);
  delay2.delayTime.value = 0.22;
  const fb2 = ctx.createGain();
  fb2.gain.value = 0.2;
  const wet2 = ctx.createGain();
  wet2.gain.value = 0.08;

  src.connect(lpf); lpf.connect(hpf);
  hpf.connect(dry); dry.connect(masterGain);
  hpf.connect(delay1); delay1.connect(fb1); fb1.connect(delay1);
  delay1.connect(wet1); wet1.connect(masterGain);
  hpf.connect(delay2); delay2.connect(fb2); fb2.connect(delay2);
  delay2.connect(wet2); wet2.connect(masterGain);

  src.start();
  activeNodes.push(src, lpf, hpf, dry, delay1, delay2, fb1, fb2, wet1, wet2);
}

// ── FOREST ──────────────────────────────────────────────────
// Soft pink noise (leaves/wind) + random bird chirps (FM oscillators)

function startForest(ctx, masterGain) {
  // Soft wind/leaf rustle
  const pink = buildPinkBuffer(ctx, 4);
  const src = makeLoopSource(ctx, pink);
  const lpf = ctx.createBiquadFilter();
  lpf.type = 'lowpass';
  lpf.frequency.value = 4000;
  const windGain = ctx.createGain();
  windGain.gain.value = 0.18;

  // Very slow wind LFO
  const lfo = ctx.createOscillator();
  const lfoG = ctx.createGain();
  lfo.frequency.value = 0.05;
  lfoG.gain.value = 0.06;
  lfo.connect(lfoG); lfoG.connect(windGain.gain);

  src.connect(lpf); lpf.connect(windGain); windGain.connect(masterGain);
  src.start(); lfo.start();
  activeNodes.push(src, lpf, windGain, lfo, lfoG);

  // Bird chirps
  function scheduleBirds() {
    if (!audioCtx) return;
    const now = ctx.currentTime;
    // 1-3 chirps in the next call
    const chirpCount = 1 + Math.floor(Math.random() * 2);
    for (let c = 0; c < chirpCount; c++) {
      const delay = Math.random() * 3;
      const t = now + delay;
      const baseFreq = 1800 + Math.random() * 2200;
      try {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(baseFreq, t);
        osc.frequency.linearRampToValueAtTime(baseFreq * (1.2 + Math.random() * 0.5), t + 0.12);
        osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.9, t + 0.35);
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.04 + Math.random() * 0.03, t + 0.04);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
        osc.connect(g); g.connect(masterGain);
        osc.start(t); osc.stop(t + 0.45);
      } catch(_) {}
    }
    const nextIn = 1500 + Math.random() * 5000; // next batch in 1.5–6.5s
    birdTimer = setTimeout(scheduleBirds, nextIn);
  }
  scheduleBirds();
}

// ── Start / stop ambient sound ──────────────────────────────

export function startNoise(type, volume = 0.5) {
  stopNoise();
  if (type === 'none') return;
  try {
    const ctx = getCtx();
    if (ctx.state === 'suspended') ctx.resume();

    noiseGain = ctx.createGain();
    noiseGain.gain.value = volume * 0.5;
    noiseGain.connect(ctx.destination);

    if      (type === 'ocean')    startOcean(ctx, noiseGain);
    else if (type === 'campfire') startCampfire(ctx, noiseGain);
    else if (type === 'cafe')     startCafe(ctx, noiseGain);
    else if (type === 'forest')   startForest(ctx, noiseGain);
  } catch(e) { console.warn('Audio error:', e); }
}

export function stopNoise() {
  clearTimeout(crackleTimer); crackleTimer = null;
  clearTimeout(birdTimer);    birdTimer = null;
  try {
    activeNodes.forEach(n => {
      try { if (n.stop) n.stop(); } catch(_) {}
      try { n.disconnect(); } catch(_) {}
    });
    activeNodes = [];
    if (noiseGain) { try { noiseGain.disconnect(); } catch(_) {} noiseGain = null; }
  } catch(_) {}
}

export function setNoiseVolume(volume) {
  if (noiseGain) noiseGain.gain.value = volume * 0.5;
}

// ── Chime helpers ──────────────────────────────────────────

function playTones(freqs, spacing = 0.15, duration = 0.6, vol = 0.25) {
  try {
    const ctx = getCtx();
    if (ctx.state === 'suspended') ctx.resume();
    freqs.forEach((freq, i) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t = ctx.currentTime + i * spacing;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(vol, t + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, t + duration);
      osc.start(t);
      osc.stop(t + duration);
    });
  } catch(e) { console.warn('Audio error:', e); }
}

export function playStartChime() {
  playTones([523.25, 659.25, 783.99], 0.15, 0.55, 0.22);
}

export function playEndChime() {
  playTones([880, 783.99, 659.25, 523.25], 0.2, 0.9, 0.28);
}

export function playBreakChime() {
  playTones([1046.5, 880], 0.25, 0.8, 0.18);
}
